import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import hotToast from "react-hot-toast";
import api from "../../chat/api";
import ReviewerActionGate from "../../../components/ReviewerActionGate";
import { Spinner } from "./FolderPickerModal";
import { DeleteIcon, PreviewIcon, ThreeDotsIcon } from "./Icons";
import useNewVersionUpload from "./NewVersionUpload";
import { STALE_AFTER_MONTHS } from "./kbHelpers";

const FILTERS = [
  [6, "6 months"],
  [12, "12 months"],
  [24, "24 months"],
];

const fileIcon = (name = "") => {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf") return "/pdff.svg";
  if (ext === "xlsx" || ext === "xls") return "/Xls.svg";
  if (ext === "docx" || ext === "doc") return "/docX.svg";
  if (ext === "csv") return "/CSV.svg";
  if (ext === "txt") return "/TXT.svg";
  return "/pdf.svg";
};

const ageLabel = (months) => (months >= 24 ? `${Math.floor(months / 12)} yr ${months % 12} mo` : `${months} months`);
const ageStyle = (months) =>
  months >= 18
    ? { color: "#A3141B", background: "#FDECEC", borderColor: "#E58A8A" }
    : { color: "#7A5B00", background: "#FFF6D6", borderColor: "#D9B53A" };

const GRID_FULL = "grid grid-cols-[minmax(160px,1fr)_140px_110px_100px_340px] items-center";
const GRID_COMPACT = "grid grid-cols-[minmax(110px,1fr)_minmax(70px,110px)_80px_96px_88px] items-center";
// Below this table width the row actions move into a "..." menu.
const COMPACT_BELOW = 860;

// Stale Assets page: ingested documents that haven't been updated for the chosen time.
const StaleAssetsPage = ({ isReviewer, onBack, onCountChange, onDelete, onReplaced, onOpenDocument, refreshKey, formatDate }) => {
  const [months, setMonths] = useState(STALE_AFTER_MONTHS);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const latestRequest = useRef(0);
  const tableRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const [menu, setMenu] = useState(null);
  const pad = compact ? "px-2" : "px-3";

  // Measured before the first paint so narrow screens never flash the wide layout.
  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return undefined;
    setCompact(el.getBoundingClientRect().width < COMPACT_BELOW);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setCompact(entry.contentRect.width < COMPACT_BELOW));
    observer.observe(el);
    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const openMenu = (event, item) => {
    event.stopPropagation();
    if (menu?.item.id === item.id) return setMenu(null);
    const r = event.currentTarget.getBoundingClientRect();
    const up = r.bottom + 140 > window.innerHeight;
    return setMenu({ item, right: window.innerWidth - r.right, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };

  const orgParams = () => {
    const orgId = Cookies.get("selected_org_id");
    return orgId ? { organization_id: orgId } : {};
  };

  const load = useCallback(async () => {
    // A slower answer for an earlier filter must not replace the current one.
    const request = ++latestRequest.current;
    setError("");
    try {
      const params = new URLSearchParams({ ...orgParams(), months: String(months) });
      const res = await api.get(`/api/v1/rfp/knowledge-base/stale/?${params.toString()}`);
      if (request !== latestRequest.current) return;
      setItems(res.data?.results || []);
      if (months === STALE_AFTER_MONTHS) onCountChange?.(res.data?.count || 0);
    } catch (err) {
      if (request !== latestRequest.current) return;
      setItems([]);
      setError(err?.response?.data?.error || "Couldn't load stale documents.");
    }
  }, [months, onCountChange]);

  useEffect(() => {
    setItems(null);
    load();
  }, [load, refreshKey]);

  const dropItem = (id) => {
    setItems((prev) => (prev || []).filter((i) => i.id !== id));
    // The card counts the shortest filter, which holds every stale document.
    onCountChange?.((n) => Math.max(0, n - 1));
  };

  const markStillValid = async (item) => {
    const toastId = hotToast.loading("Saving...");
    setBusyId(item.id);
    try {
      await api.post(`/api/v1/rfp/knowledge-base/documents/${item.id}/still-valid/`, orgParams());
      dropItem(item.id);
      hotToast.success(`“${item.name}” marked as still valid`, { id: toastId });
    } catch (err) {
      hotToast.error(err?.response?.data?.error || "Couldn't update this document.", { id: toastId });
    } finally {
      setBusyId(null);
    }
  };

  const newVersion = useNewVersionUpload({
    onUploaded: (item, data) => {
      dropItem(item.id);
      onReplaced?.(data);
    },
  });

  return (
    <div className="flex-1 min-h-0 flex flex-col font-outfit">
      {newVersion.element}
      {menu && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: menu.top, bottom: menu.bottom, right: menu.right }}
          className="z-[1000] w-[200px] p-1 bg-white border border-[#D4D4D8] rounded-lg shadow-[0_8px_24px_rgba(24,16,28,0.16)] flex flex-col font-outfit"
        >
          {[
            ["Still valid", () => markStillValid(menu.item)],
            ["Upload new version", () => newVersion.pick(menu.item)],
            ["Delete", () => onDelete?.(menu.item), true],
          ].map(([label, run, danger]) => (
            <ReviewerActionGate key={label} active={isReviewer} block place="left">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(null);
                  run();
                }}
                className={`w-full text-left px-3 py-2 rounded-md text-[14px] font-medium hover:bg-[#F4F4F5] ${
                  danger ? "text-[#B42318]" : "text-[#27272A]"
                }`}
              >
                {label}
              </button>
            </ReviewerActionGate>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-[10px] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            title="Back to All Files"
            className="w-[30px] h-[30px] flex items-center justify-center border border-[#A1A1AA] rounded-md bg-white hover:border-[#A04A97]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#52525B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span onClick={onBack} className="text-[15px] font-medium text-[#52525B] cursor-pointer hover:text-[#A04A97]">
            All Files
          </span>
          <svg width="6" height="9" viewBox="0 0 6 9" fill="#52525B">
            <path d="M3.37 4.5.22 1.35a.79.79 0 0 1 1.13-1.13l3.71 3.71a.8.8 0 0 1 0 1.13L1.35 8.78A.79.79 0 0 1 .22 7.65L3.37 4.5z" />
          </svg>
          <span className="text-[15px] font-semibold text-[#18181B]">Stale Assets</span>
        </div>

        <div className="flex items-end justify-between gap-4 border-b border-[#D4D4D8] flex-wrap">
          <div className="flex items-center gap-2 -mb-px px-3 pt-2 pb-[10px] border-b-2 border-[#A04A97] text-[15px] font-semibold text-[#A04A97] whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-[#52525B]" />
            Stale Assets
            <span className="px-[7px] rounded-full bg-[#F7E9F5] text-[12px] font-semibold leading-[18px] text-[#6E2A67]">
              {items === null ? "…" : items.length}
            </span>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <span className="text-[13px] font-medium text-[#3F3F46] whitespace-nowrap">Older than</span>
            <div className="flex p-[2px] border border-[#A1A1AA] rounded-md bg-white">
              {FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setMonths(value)}
                  className={`px-3 py-1 rounded text-[13px] font-medium whitespace-nowrap ${
                    months === value ? "bg-[#A04A97] text-white" : "text-[#27272A] hover:bg-[#F4F4F5]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="m-0 text-[14px] leading-[1.5] text-[#3F3F46]">
          Ingested documents that haven't been updated in a while. Certificates, financials and bios age fastest. Upload a
          new version so proposals don't cite expired information.
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pt-3 pb-2">
        {items === null && (
          <div className="flex items-center gap-2 py-10 justify-center text-[14px] text-[#71717A]">
            <Spinner color="#A04A97" /> Loading stale documents...
          </div>
        )}
        {items !== null && error && <p className="m-0 py-10 text-center text-[14px] text-[#B42318]">{error}</p>}
        {items !== null && !error && items.length === 0 && (
          <div className="py-16 text-center">
            <p className="m-0 mb-1.5 text-[17px] font-semibold text-[#18181B]">Nothing older than {months} months</p>
            <p className="m-0 text-[14px] text-[#3F3F46]">Every ingested document has been updated recently.</p>
          </div>
        )}
        {items !== null && !error && items.length > 0 && (
          <div ref={tableRef} className="border border-[#D4D4D8] rounded-lg overflow-x-auto">
            <div className={compact ? "min-w-[444px]" : "min-w-[850px]"}>
              <div className={`${compact ? GRID_COMPACT : GRID_FULL} h-10 bg-[#F4F4F5] border-b border-[#A1A1AA] text-[13px] font-semibold text-[#27272A]`}>
                <span className={compact ? "px-3" : "px-4"}>Document</span>
                <span className={pad}>Folder</span>
                <span className={pad}>Last updated</span>
                <span className={pad}>Age</span>
                <span />
              </div>
              {items.map((item) => {
                const busy = busyId === item.id || newVersion.busyId === item.id;
                const folderNames = item.folders.map((f) => f.name).join(", ");
                return (
                  <div
                    key={item.id}
                    className={`${compact ? GRID_COMPACT : GRID_FULL} min-h-[52px] border-b border-[#D4D4D8] last:border-b-0 hover:bg-[#F8F3FF] ${
                      busy ? "opacity-60 pointer-events-none" : ""
                    }`}
                  >
                    <div className={`min-w-0 ${compact ? "px-3" : "px-4"} flex items-center gap-[10px]`}>
                      <img src={fileIcon(item.name)} alt="" className="w-6 h-6 flex-shrink-0" />
                      <span title={item.name} className="min-w-0 truncate text-[14px] font-medium text-[#18181B]">
                        {item.name}
                      </span>
                      {busy && <Spinner size={14} color="#A04A97" />}
                    </div>
                    <span title={folderNames} className={`min-w-0 ${pad} truncate text-[14px] text-[#27272A]`}>
                      {folderNames || "No folder"}
                    </span>
                    <span className={`${pad} text-[14px] text-[#27272A] whitespace-nowrap`}>{formatDate(item.last_updated)}</span>
                    <div className={pad}>
                      <span
                        className="inline-flex px-[10px] py-[2px] rounded-full border text-[12px] font-semibold whitespace-nowrap"
                        style={ageStyle(item.age_months)}
                      >
                        {ageLabel(item.age_months)}
                      </span>
                    </div>
                    <div className={`${compact ? "pl-2 pr-3" : "pl-3 pr-4"} flex items-center justify-end gap-2`}>
                      {item.file_url && (
                        <button
                          type="button"
                          onClick={() => onOpenDocument?.(item)}
                          title="Preview document"
                          aria-label={`Preview ${item.name}`}
                          className="w-[30px] h-[30px] flex-shrink-0 flex items-center justify-center border border-[#A1A1AA] rounded-md bg-white hover:bg-[#F4F4F5]"
                        >
                          <PreviewIcon />
                        </button>
                      )}
                      {compact && (
                        <button
                          type="button"
                          onClick={(e) => openMenu(e, item)}
                          title="More actions"
                          aria-label={`More actions for ${item.name}`}
                          aria-expanded={menu?.item.id === item.id}
                          className="w-[30px] h-[30px] flex-shrink-0 flex items-center justify-center border border-[#A1A1AA] rounded-md bg-white text-[#52525B] hover:bg-[#F4F4F5]"
                        >
                          <ThreeDotsIcon />
                        </button>
                      )}
                      {!compact && (
                      <>
                      <ReviewerActionGate active={isReviewer} place="left">
                        <button
                          type="button"
                          onClick={() => markStillValid(item)}
                          title="It's still current; check it again later"
                          className="px-3 py-[5px] border border-[#A1A1AA] rounded-md bg-white text-[13px] font-medium text-[#27272A] hover:bg-[#F4F4F5] whitespace-nowrap"
                        >
                          Still valid
                        </button>
                      </ReviewerActionGate>
                      <ReviewerActionGate active={isReviewer} place="left">
                        <button
                          type="button"
                          onClick={() => newVersion.pick(item)}
                          className="px-3 py-[5px] border border-[#A04A97] rounded-md bg-white text-[13px] font-semibold text-[#A04A97] hover:bg-[#FDF2F8] whitespace-nowrap"
                        >
                          Upload new version
                        </button>
                      </ReviewerActionGate>
                      <ReviewerActionGate active={isReviewer} place="left">
                        <button
                          type="button"
                          onClick={() => onDelete?.(item)}
                          title="Delete document"
                          className="w-[30px] h-[30px] flex items-center justify-center border border-[#A1A1AA] rounded-md bg-white text-red-500 hover:border-red-400"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <DeleteIcon />
                          </span>
                        </button>
                      </ReviewerActionGate>
                      </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaleAssetsPage;
